/**
 * Wire fetches for the topic-links lens (topicFlows.js has the math).
 *
 * Both reads go through runQuery, so the admin generation pin and the
 * latest-completed-generation default apply automatically — the lens
 * always describes ONE clustering pass, the same one the rest of the
 * dashboard is showing.
 *
 * The pair fetch groups by (session, topic) SERVER-SIDE by attaching a
 * count metric — the same dummy-metric idiom the backend's own
 * search_prior_work co-occurrence pass uses (a dimensions-only spec
 * returns one row per prompt, not per pair). Rows come back at
 * pair grain: sessions × topics-per-session, not prompts.
 */
import {
    rangeToTimeRange,
    rowsToObjects,
    runQuery,
} from "./analyticsClient.js";

// Far above the pair count a real tenant window produces (staging ≈45k
// at 66k prompts), far below the API's 100k single-page ceiling. If a
// tenant ever hits this, the lens under-reports rather than erroring —
// surfaced via the `truncated` flag so the body can say so.
const PAIR_LIMIT = 100000;

const EC = "ec";
const ecField = (field) => ({ stepAlias: EC, field });

export async function fetchSessionTopicPairs(range, { signal, scope } = {}) {
    // `range` is the dashboard's GLOBAL date picker, deliberately respected
    // (the whole panel — counts, evidence — scopes to one range). What the
    // lens ignores is the X-ray scan window's sampled prompts, not the
    // picker.
    //
    // `scope` is the pushdown half of topicScopePlan (topicFlows.js): the
    // event-cluster entity carries endpoint and session ids directly, so
    // endpoint/session scope steps filter the corpus server-side. Other
    // scope dims intersect client-side via the session-set resolution.
    const inPred = (field, ids) => ({
        pred: {
            field: ecField(field),
            op: ids.length > 1 ? "OPERATOR_IN" : "OPERATOR_EQ",
            value: ids.length > 1 ? ids : ids[0],
        },
    });
    const scopePreds = [
        ...(scope?.endpoints?.length ? [inPred("DIMENSION_FIELD_ILO_EVENT_CLUSTER_ENDPOINT_ID", scope.endpoints)] : []),
        ...(scope?.sessions?.length ? [inPred("DIMENSION_FIELD_ILO_EVENT_CLUSTER_NOTIFICATION_ID", scope.sessions)] : []),
    ];
    const res = await runQuery({
        root: { alias: EC, entity: "ENTITY_TYPE_ILO_EVENT_CLUSTER" },
        timeRange: rangeToTimeRange(range),
        filter: {
            andExpr: {
                children: [
                    { pred: { field: ecField("DIMENSION_FIELD_ILO_EVENT_CLUSTER_IS_OUTLIER"), op: "OPERATOR_EQ", value: false } },
                    { pred: { field: ecField("DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_ID"), op: "OPERATOR_GT", value: 0 } },
                    ...scopePreds,
                ],
            },
        },
        output: {
            dimensions: [
                { field: ecField("DIMENSION_FIELD_ILO_EVENT_CLUSTER_NOTIFICATION_ID"), outputAlias: "session" },
                { field: ecField("DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_ID"), outputAlias: "clusterId" },
                // Notification ids are endpoint-scoped, so this doesn't change
                // the (session, topic) grain — it just carries WHERE each
                // session ran, for the pattern endpoint-mix strip.
                { field: ecField("DIMENSION_FIELD_ILO_EVENT_CLUSTER_ENDPOINT_ID"), outputAlias: "endpoint" },
            ],
            // Forces GROUP BY (session, topic); the count itself is unused.
            metrics: [{
                outputAlias: "events",
                computed: { field: ecField("DIMENSION_FIELD_ILO_EVENT_CLUSTER_POINT_ID"), op: "AGGREGATE_OP_COUNT" },
            }],
        },
        limit: PAIR_LIMIT,
    }, { signal });
    const rows = rowsToObjects(res);
    return { rows, truncated: rows.length >= PAIR_LIMIT };
}

// (Per-topic evidence is NOT fetched here: the lens reuses the shared
// `getTraces` method so evidence rows carry the full trace-log treatment
// and click through to the trace detail.)

// endpoint id → hostname for the pattern endpoint-mix strip, scoped to the
// exact ids on display (a handful per pattern) — never a whole-fleet page,
// which a Large tenant (5k+ endpoints) would silently overflow. ENDPOINT
// has no time field, so the timeRange is ignored on this root (same as the
// inventory aggregation path).
export async function fetchEndpointHostnames({ ids, signal } = {}) {
    const wanted = (ids ?? []).filter(Boolean).map(String);
    if (!wanted.length) return new Map();
    const idField = { stepAlias: "e", field: "DIMENSION_FIELD_ENDPOINT_ENDPOINT_ID" };
    const res = await runQuery({
        root: { alias: "e", entity: "ENTITY_TYPE_ENDPOINT" },
        timeRange: rangeToTimeRange(null),
        filter: {
            pred: {
                field: idField,
                op: wanted.length > 1 ? "OPERATOR_IN" : "OPERATOR_EQ",
                value: wanted.length > 1 ? wanted : wanted[0],
            },
        },
        output: {
            dimensions: [
                { field: idField, outputAlias: "id" },
                { field: { stepAlias: "e", field: "DIMENSION_FIELD_ENDPOINT_HOSTNAME" }, outputAlias: "hostname" },
            ],
        },
        limit: wanted.length,
    }, { signal });
    const byId = new Map();
    for (const r of rowsToObjects(res)) {
        if (r.id != null && r.hostname) byId.set(String(r.id), String(r.hostname));
    }
    return byId;
}
